import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { api } from 'store/api';
import { onSignOut } from 'store/auth/reducers';
import { PersistService } from 'utils/persist';

import type { OrganizationState } from './types';

const selectedOrganizationId = PersistService.get<string>('organization.selected.id');
const impersonationViewOrganizationId = PersistService.getImpersonatedOrganization();

const initialState: OrganizationState = {
  selectedOrganizationId,
  impersonationViewOrganizationId,
};

const slice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    setSelectedOrganizationId: (state, { payload }: PayloadAction<string | null>) => {
      state.selectedOrganizationId = payload;

      PersistService.set('organization.selected.id', payload);
    },

    // Impersonation cannot call PUT /organisations/active (backend returns 409), so the viewed
    // org during impersonation lives only in this request-scoped field, mirrored to the
    // X-Switch-Organisation header rather than persisted server-side.
    setImpersonationViewOrganizationId: (state, { payload }: PayloadAction<string | null>) => {
      state.impersonationViewOrganizationId = payload;

      PersistService.setImpersonatedOrganization(payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(onSignOut, (state) => {
      state.selectedOrganizationId = null;
      state.impersonationViewOrganizationId = null;
      PersistService.remove('organization.selected.id');
      PersistService.remove('X-Switch-Organisation');
    });

    builder.addMatcher(api.endpoints.getSession.matchFulfilled, (state, { payload }) => {
      state.selectedOrganizationId = payload.organization?.id ?? null;
      PersistService.set('organization.selected.id', payload.organization?.id ?? null);
    });
  },
});

export const { setSelectedOrganizationId, setImpersonationViewOrganizationId } = slice.actions;

export const { reducer: organizationsReducer } = slice;
